import React, { useEffect, useMemo, useRef, useState } from "react";
import metricMetadataIcon from "../../../assets/metric-metadata-icon-blue.svg";
import MetricMetadataModal from "../../MetricMetadataModal";
import {
  DisaggregatedMetric,
  DistributionData,
  GeoDetails,
  GeoTag,
  MetricMetadata,
  RawIndicatorData,
} from "../../../services/types";
import {
  fetchDistributionData,
  fetchIndicatorBinCount,
  fetchMetricMetadata,
} from "../../../utils/dashboardUtils";
import { GaugeChart } from "./GaugeChart";
import { FunctionalClassBreakdownChart } from "./FuntionalClassBreakdownChart";

interface RoadQualityChartsProps {
    metric?: DisaggregatedMetric;
    disaggData: DisaggregatedMetric[];
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    showMetadataIcon?: boolean;
}

const BAR_COLOR = "#1F3B73";

const RoadQualityCharts: React.FC<RoadQualityChartsProps> = ({
    metric,
    disaggData,
    geoDetails,
    geoTag,
    selectionActive,
    showMetadataIcon = true,
}) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const [showBreakdownDialog, setShowBreakdownDialog] = useState(false);
    const breakdownTriggerRef = useRef<HTMLButtonElement | null>(null);
    const breakdownDialogId = `road-quality-breakdown-dialog-${metric?.id ?? "default"}`;

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const clickDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});
    const modalId = `metric-metadata-modal-${metric?.id ?? "road-quality"}`;

    const version = useMemo(() => {
        if (!metric) return null;
        return (
        metric.versions.find((v) => v.is_default === "yes") ?? metric.versions[0]
        );
    }, [metric]);

    const barData = useMemo(() => {
        if (!version) return [];
        return [...version.disaggregations]
        .sort((a, b) => a.value - b.value)
        .map((d) => ({ ...d, color: BAR_COLOR }));
    }, [version]);

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
        return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
        delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    const handleMetadataClick = async (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (!metric?.id) return;

        triggerRef.current = e.currentTarget;

        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
        setShowMetadata(true);
        setLoading(true);
        setError(null);

        try {
            const id = metric.id;

            const [metadataResponse, distributionResponse, countyDistributionResponse] =
            await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),
                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(
                    `distData-${id}-${geoDetails.map((d) => d.lod).join(",")}-${geoTag?.type}-${geoTag?.value}`,
                    () => fetchIndicatorBinCount(id, geoDetails, geoTag)
                    ),
                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${id}-default`, () =>
                    fetchDistributionData(id, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionResponse || null);
            if (!countyDistributionData) {
            setCountyDistributionData(countyDistributionResponse || null);
            }
        } catch (err) {
            const errorMessage =
            (err as Error).message || "An unexpected error occurred.";
            setError(new Error(`Failed to load data: ${errorMessage}`));
        } finally {
            setLoading(false);
        }
        }, 150);
    };

    const closeMetadata = () => {
        setShowMetadata(false);
    };

    const openBreakdownDialog = (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        breakdownTriggerRef.current = e.currentTarget;
        setShowBreakdownDialog(true);
    };

    const closeBreakdownDialog = () => {
        setShowBreakdownDialog(false);
        breakdownTriggerRef.current?.focus();
    };

    useEffect(() => {
        return () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);
        };
    }, []);

    useEffect(() => {
        if (!showBreakdownDialog) return;

        const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            closeBreakdownDialog();
        }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showBreakdownDialog]);

    if (!metric || !version) return null;

    return (
        <>
        <div className="relative overflow-hidden border rounded-xl bg-white shadow-sm">
            <div className="flex items-start justify-between px-4 pt-4 pb-2">
            <div>
                <h3 className="text-sm font-semibold text-port-text">
                Average Road Quality
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                Overall PCI and breakdown by functional class
                </p>
            </div>

            {showMetadataIcon && (
                <button
                type="button"
                onClick={handleMetadataClick}
                aria-haspopup="dialog"
                aria-expanded={showMetadata ? "true" : "false"}
                aria-controls={modalId}
                aria-label={`Additional information about ${metric.name}`}
                className="inline-flex h-6 w-6 items-center justify-center rounded transition-transform hover:scale-105 focus:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900"
                >
                <img
                    src={metricMetadataIcon}
                    alt=""
                    aria-hidden="true"
                    className="h-3 w-3"
                    draggable="false"
                />
                </button>
            )}
            </div>

            <div className="px-4 pb-4">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 flex items-center justify-center">
                <GaugeChart value={version.value} />
                </div>

                <button
                type="button"
                ref={breakdownTriggerRef}
                onClick={openBreakdownDialog}
                aria-haspopup="dialog"
                aria-expanded={showBreakdownDialog ? "true" : "false"}
                aria-controls={breakdownDialogId}
                aria-label="Open functional class breakdown for average road quality"
                className="w-[120px] shrink-0 rounded-lg border border-slate-200 bg-slate-50 p-3 text-left shadow-sm transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900"
                >
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Breakdown
                </div>

                <div className="space-y-1.5" aria-hidden="true">
                    {barData.map((item) => (
                    <div key={item.id} className="h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div
                        className="h-full rounded-full"
                        style={{
                            width: `${item.value}%`,
                            backgroundColor: BAR_COLOR,
                        }}
                        />
                    </div>
                    ))}
                </div>

                <div className="mt-3 text-xs font-medium text-slate-700">
                    View details
                </div>
                </button>
            </div>
            </div>
        </div>

        <MetricMetadataModal
            isOpen={showMetadata}
            onClose={closeMetadata}
            id={metric.id}
            metadata={metadata}
            stateData={null}
            nationalData={null}
            disaggData={disaggData}
            distributionData={distributionData}
            countyDistributionData={countyDistributionData}
            selectionActive={selectionActive}
            loading={loading}
            error={error}
            triggerRef={triggerRef}
            modalId={modalId}
        />

        {showBreakdownDialog && (
            <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="presentation"
            onClick={closeBreakdownDialog}
            >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={breakdownDialogId}
                className="w-full max-w-3xl rounded-xl bg-white p-4 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                    <h2
                    id={breakdownDialogId}
                    className="text-sm font-semibold text-port-text"
                    >
                    Functional Class Breakdown
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">
                    Pavement condition index by roadway functional class
                    </p>
                </div>

                <button
                    type="button"
                    onClick={closeBreakdownDialog}
                    className="rounded p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900"
                    aria-label="Close functional class breakdown dialog"
                >
                    ✕
                </button>
                </div>

                <div className="h-[280px]">
                <FunctionalClassBreakdownChart barData={barData} />
                </div>
            </div>
            </div>
        )}
        </>
    );
};

export default RoadQualityCharts;